// src/frontend/src/components/Common/MobileUpgradeFloatingButton.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';

interface UserPlan {
  planType: string;
  creditsUsed: number;
  creditsTotal: number;
  isTrialActive: boolean;
  trialDaysLeft: number;
}

const MobileUpgradeFloatingButton: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPlan();
    }

    // Check if dismissed in this session
    const dismissed = sessionStorage.getItem('mobileFABDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }

    // Show after user scrolls down a bit (mobile behavior)
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setIsExpanded(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsExpanded(false);
    sessionStorage.setItem('mobileFABDismissed', 'true');
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Don't show on certain pages
  const hideOnPages = ['/pricing', '/register', '/login', '/payment-success', '/payment-cancelled'];
  const shouldHideOnCurrentPage = hideOnPages.some(page => location.pathname.startsWith(page));

  // Don't show if not authenticated, dismissed, already on premium plan, or on certain pages
  if (!isAuthenticated || isDismissed || !userPlan || shouldHideOnCurrentPage) return null;
  
  // Only show for trial users or Solo Core users
  const shouldShow = userPlan.isTrialActive || userPlan.planType === 'solo_core';
  if (!shouldShow) return null;

  const creditsPercentageUsed = (userPlan.creditsUsed / userPlan.creditsTotal) * 100;
  const isUrgent = userPlan.isTrialActive && userPlan.trialDaysLeft <= 3;
  const isLowCredits = creditsPercentageUsed >= 80;

  // Determine button style based on urgency
  const getButtonStyle = () => {
    if (isUrgent) return 'bg-gradient-to-r from-red-500 to-pink-600 shadow-red-200';
    if (isLowCredits) return 'bg-gradient-to-r from-orange-500 to-red-500 shadow-orange-200';
    return 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-blue-200';
  };

  const getButtonText = () => {
    if (isUrgent) return userPlan.trialDaysLeft === 1 ? 'Expires Tomorrow!' : `${userPlan.trialDaysLeft} Days Left`;
    if (userPlan.isTrialActive) return 'Upgrade Trial';
    if (userPlan.planType === 'solo_core') return 'Upgrade to Pro';
    return 'Upgrade';
  };

  return (
    <>
      {/* Mobile FAB - Only visible on mobile */}
      <div className={`md:hidden fixed bottom-6 right-4 z-50 transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0 pointer-events-none'
      }`}>
        
        {/* Expanded Panel */}
        {isExpanded && (
          <div className={`absolute bottom-16 right-0 w-80 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl p-4 text-white transform transition-all duration-300 ${
            isExpanded ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          } ${getButtonStyle()}`}>
            
            {/* Close button */}
            <button
              onClick={handleToggleExpand}
              className="absolute top-2 right-2 text-white/70 hover:text-white"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Content */}
            <div className="pr-6">
              <h3 className="font-bold text-lg mb-2">
                {isUrgent ? 'Trial Expiring!' : 
                 userPlan.planType === 'solo_core' ? 'Upgrade to Pro' : 'Upgrade Now'}
              </h3>
              
              <p className="text-white/90 text-sm mb-3">
                {isUrgent 
                  ? `Only ${userPlan.trialDaysLeft} day${userPlan.trialDaysLeft === 1 ? '' : 's'} left! Don't lose your progress.`
                  : userPlan.planType === 'solo_core'
                  ? 'Get 3x more sessions + real-time coaching tips!'
                  : 'Unlock unlimited access to all PM coaching features.'
                }
              </p>

              {/* Quick stats */}
              <div className="bg-white/20 rounded-lg p-2 mb-3 text-xs">
                <div className="flex justify-between mb-1">
                  <span>Sessions:</span>
                  <span>{userPlan.creditsUsed}/{userPlan.creditsTotal}</span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-1.5">
                  <div 
                    className="bg-white h-1.5 rounded-full transition-all duration-300" 
                    style={{ width: `${creditsPercentageUsed}%` }}
                  ></div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => createCheckoutSession(
                    userPlan.planType === 'solo_core' ? 'solo_pro' : 'solo_pro', 
                    'monthly'
                  )}
                  disabled={isCreatingCheckout}
                  className="w-full bg-white text-blue-600 py-2.5 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm disabled:opacity-50"
                >
                  {isCreatingCheckout ? 'Setting up...' : 
                   userPlan.planType === 'solo_core' ? 'Upgrade to Pro - $39' : 'Get Pro - $39/mo'}
                </button>
                
                {userPlan.isTrialActive && (
                  <button
                    onClick={() => createCheckoutSession('solo_core', 'monthly')}
                    disabled={isCreatingCheckout}
                    className="w-full bg-white/20 backdrop-blur-sm py-2 px-4 rounded-lg font-medium hover:bg-white/30 transition-colors text-sm disabled:opacity-50"
                  >
                    {isCreatingCheckout ? 'Setting up...' : 'Start Core - $19/mo'}
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-transparent border border-white/50 py-2 px-4 rounded-lg font-medium hover:bg-white/10 transition-colors text-sm"
                >
                  View All Plans
                </button>
              </div>
            </div>

            {/* Dismiss option */}
            <button
              onClick={handleDismiss}
              className="w-full text-center text-white/70 text-xs mt-3 pt-2 border-t border-white/20"
            >
              Don't show again
            </button>
          </div>
        )}

        {/* Main FAB Button */}
        <button
          onClick={handleToggleExpand}
          className={`relative flex items-center justify-center px-4 py-3 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm ${getButtonStyle()} ${
            isExpanded ? 'scale-110' : 'hover:scale-105'
          }`}
        >
          {/* Pulse animation for urgent situations */}
          {isUrgent && (
            <div className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-20"></div>
          )}
          
          <div className="flex items-center space-x-2 relative z-10">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <span className="whitespace-nowrap">{getButtonText()}</span>
            {!isExpanded && (
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            )}
          </div>
        </button>
      </div>
    </>
  );
};

export default MobileUpgradeFloatingButton;